import styles from "./style.module.scss";
import React, {useMemo} from "react";
import {IPropsElement, separatorOpts} from "./constants";
import {InputNumber, Select, Switch} from "antd";
import {signers} from "../../../util/varibles/constants";

interface IProps {
    onChange(value: Partial<IPropsElement>): void;
}

const Label: React.FC<{ label: string; name?: string } & IProps> = ({
                                                                        label,
                                                                        name = "Label",
                                                                        onChange,
                                                                    }) => {
    return (
        <div className={styles.rightProperty}>
            <label>{name}</label>
            <input
                type="text"
                data-checklist-property="label"
                value={label}
                onChange={({target}) => onChange({label: target.value || ""})}
            />
            <span>Type a text field label.</span>
        </div>
    );
};

const SubLabel: React.FC<{ properties: IPropsElement["properties"]; name?: string } & IProps> = ({
                                                                                                     properties,
                                                                                                     name = "Sub label",
                                                                                                     onChange
                                                                                                 }) => {
    return (
        <div className={styles.rightProperty}>
            <label>{name}</label>
            <input
                type="text"
                data-checklist-property="subLabel"
                value={properties.subLabel}
                onChange={({target}) =>
                    onChange({
                        properties: {...properties, subLabel: target.value},
                    })
                }
            />
            <span>Add a small description below the input field.</span>
        </div>
    );
};

const Require: React.FC<{ properties: IPropsElement["properties"] } & IProps> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <div className="flex gap-1 items-center">
                <label className="mt-[6px]">Required field: </label>
                <Switch
                    value={properties.required}
                    onChange={(checked) =>
                        onChange({
                            properties: {...properties, required: checked},
                        })
                    }
                />
            </div>
            <span>Prevent submission if this question is empty.</span>
        </div>
    );
};

const Options: React.FC<{ properties: IPropsElement["properties"] } & IProps> = ({properties, onChange}) => {
    return <div className={styles.rightProperty}>
        <label>Options</label>
        <textarea
            value={properties.options}
            onChange={({target}) =>
                onChange({
                    properties: {...properties, options: target.value},
                })
            }
        />
        <span>Type your options to select from. Separate each on a new line.</span>
    </div>;
};

const NumberToColumns: React.FC<{ properties: IPropsElement["properties"] } & IProps> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <label>Number to columns</label>
            <InputNumber
                min={1}
                value={properties.numberOfColumns}
                onChange={value => onChange({
                    properties: {
                        ...properties,
                        numberOfColumns: (value || 1) > 0 ? value || 1 : 1
                    }
                })}
            />
            <span>Spread options side by side into specified number of columns.</span>
        </div>
    );
};
const SelectedDefault: React.FC<{ properties: IPropsElement["properties"], mode?: "multiple" } & IProps> = ({
                                                                                                                properties,
                                                                                                                mode,
                                                                                                                onChange
                                                                                                            }) => {
    const {optionDefault, options} = properties;

    return (
        <div className={styles.rightProperty}>
            <label>Selected by default</label>
            <Select
                className={mode ? "select-mode-tag" : ""}
                value={((optionDefault || "").length === 0 || optionDefault === JSON.stringify("")) ? null : optionDefault}
                mode={mode}
                options={options
                    ? options
                        .split('\n')
                        .filter(item => item.length > 0)
                        .map((item, index: number) => ({
                            value: index.toString(),
                            label: item
                        }))
                    : []}
                onChange={value => onChange({properties: {...properties, optionDefault: value}})}
            />
            <span>Choose an option to be selected by default.</span>
        </div>
    );
};

const Separator: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <label>Separator</label>
            <div className={styles["options-text"]} data-size="small">
                {separatorOpts.map((item) => (
                    <div
                        data-is-active={properties.separator === item.value}
                        key={item.value}
                        onClick={() =>
                            onChange({properties: {...properties, separator: item.value}})
                        }
                    >
                        {item.label}
                    </div>
                ))}
            </div>
            <span>Select which character to use between the date fields.</span>
        </div>
    );
};
const CalendarPopup: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <div className="flex gap-1 items-center">
                <label className="mt-[6px]">Calendar popup: </label>
                <Switch
                    value={properties.isCalendarPopup}
                    onChange={(checked) =>
                        onChange({
                            properties: {...properties, isCalendarPopup: checked},
                        })
                    }
                />
            </div>
            <span>Display a date picker when interacting with the field.</span>
        </div>
    );
};

const TimeField: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <div className="flex gap-1 items-center">
                <label className="mt-[6px]">Time field: </label>
                <Switch
                    value={properties.isShowTime}
                    onChange={(checked) =>
                        onChange({
                            properties: {...properties, isShowTime: checked},
                        })
                    }
                />
            </div>
            <span>Allow users to specify a time with date.</span>
        </div>
    );
};

const IsMultiSelections: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    const handleChange = (value: boolean) => {
        let {optionDefault, options} = properties;
        const newValue = {
            multipleSelections: value,
            optionDefault,
        };

        try {
            const opts = options?.split("\n") || []
            if (value) {
                const value = opts.findIndex((_, index) => index.toString() === (optionDefault || "").toString());
                newValue.optionDefault = value !== -1 ? [value.toString()] : undefined;
            } else {
                if (Array.isArray(optionDefault)) {
                    const valueDefault = optionDefault[0] || "";
                    const value = opts.findIndex((_, index) => index.toString() === valueDefault.toString());
                    newValue.optionDefault = value !== -1 ? [value.toString()] : undefined;
                }
            }
            onChange({properties: {...properties, ...newValue}});
        } catch (error) {
            console.log("Error JSON: ", error);
            onChange({
                properties: {
                    ...properties,
                    multipleSelections: value,
                    optionDefault: undefined,
                },
            });
        }
    };

    return (
        <div className={styles.rightProperty}>
            <div className="flex gap-1 items-center">
                <label className="mt-[6px]">Multiple Selections: </label>
                <Switch
                    value={properties.multipleSelections}
                    onChange={(checked) => handleChange(checked)}
                />
            </div>
            <span>Allow users to select multiple options.</span>
        </div>
    );
};
const HeadingSize: React.FC<{ properties: IPropsElement["properties"] } & IProps> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <label>Heading Size</label>
            <div className={styles["options-text"]}>
                <div
                    data-is-active={properties.fontSize === "default"}
                    onClick={() =>
                        onChange({properties: {...properties, fontSize: "default"}})
                    }
                >
                    Default
                </div>
                <div
                    data-is-active={properties.fontSize === "large"}
                    onClick={() =>
                        onChange({properties: {...properties, fontSize: "large"}})
                    }
                >
                    Large
                </div>
                <div
                    data-is-active={properties.fontSize === "small"}
                    onClick={() =>
                        onChange({properties: {...properties, fontSize: "small"}})
                    }
                >
                    Small
                </div>
            </div>
            <span>Small text below the heading.</span>
        </div>
    );
};

const TextAlignment: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <label>Text Alignment</label>
            <div className={styles["options-text"]}>
                <div
                    data-is-active={properties.textAlign === "left"}
                    onClick={() =>
                        onChange({properties: {...properties, textAlign: "left"}})
                    }
                >
                    Left
                </div>
                <div
                    data-is-active={properties.textAlign === "center"}
                    onClick={() =>
                        onChange({properties: {...properties, textAlign: "center"}})
                    }
                >
                    Center
                </div>
                <div
                    data-is-active={properties.textAlign === "right"}
                    onClick={() =>
                        onChange({properties: {...properties, textAlign: "right"}})
                    }
                >
                    Right
                </div>
            </div>
            <span>Small text below the heading.</span>
        </div>
    );
};

const Size: React.FC<{ properties: IPropsElement["properties"] } & IProps> = ({
                                                                                  properties,
                                                                                  onChange,
                                                                              }) => {
    return (
        <div className={styles.rightProperty}>
            <label>Size</label>
            <div className={styles.rows}>
                <input
                    type="number"
                    value={properties.numberOfRows}
                    min="2"
                    onChange={({target}) =>
                        onChange({
                            properties: {...properties, numberOfRows: +target.value},
                        })
                    }
                />
                <span>Rows</span>
            </div>
            <span>Textarea height in rows.</span>
        </div>
    );
};

const Placeholder: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <label>Placeholder</label>
            <input
                type="text"
                data-checklist-property="placeHolder"
                value={properties.placeHolder}
                onChange={({target}) =>
                    onChange({
                        properties: {...properties, placeHolder: target.value},
                    })
                }
            />
            <span>Add an example hint inside the field.</span>
        </div>
    );
};

const Signer: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    const handleChange = (value: any) => {
        const data = signers.filter((item) => value.indexOf(item.value) !== -1);
        onChange({properties: {...properties, options: JSON.stringify(data)}});
    };
    const {options} = properties;
    const value = useMemo(() => {
        try {
            return JSON.parse(options || "").map(
                (item: { label: string; value: string }) => item.value
            );
        } catch (e) {
            console.log("Json error: ", e);
            return []
        }
    }, [options]);

    return (
        <div className={styles.rightProperty}>
            <label>Who need to sign</label>
            <Select
                {...{
                    className: "select-mode-tag",
                    mode: "multiple",
                    value,
                    onChange: (value: any) => handleChange(value),
                    options: signers,
                }}
            />
            <span>Select people who need to sign to checklist.</span>
        </div>
    );
};

const MinuteStep: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <label>Minute stepping</label>
            <div className="flex">
                <Select
                    defaultValue={properties.minuteStep}
                    style={{width: 120}}
                    onChange={(value) =>
                        onChange({properties: {...properties, minuteStep: value},})
                    }
                    options={[
                        {value: 1, label: "1 min"},
                        {value: 5, label: "5 min"},
                        {value: 10, label: "10 min"},
                        {value: 15, label: "15 min"},
                        {value: 30, label: "30 min"},
                    ]}
                />
            </div>
        </div>
    );
};

const TimeRange: React.FC<
    { properties: IPropsElement["properties"] } & IProps
> = ({properties, onChange}) => {
    return (
        <div className={styles.rightProperty}>
            <div className="flex gap-1 items-center">
                <label className="mt-[6px]">Time range: </label>
                <Switch
                    value={properties.isTimeRange}
                    onChange={(checked) =>
                        onChange({
                            properties: {...properties, isTimeRange: checked},
                        })
                    }
                />
            </div>
        </div>
    );
};

export {
    Label,
    SubLabel,
    Require,
    Options,
    NumberToColumns,
    SelectedDefault,
    Separator,
    CalendarPopup,
    TimeField,
    IsMultiSelections,
    HeadingSize,
    TextAlignment,
    Size,
    Placeholder,
    Signer,
    MinuteStep,
    TimeRange,
};
